<template>
  <div class="btn-group">
    <button ref="toggle"
            type="button"
            class="btn btn-primary dropdown-toggle"
            :aria-expanded="isOpen.toString()"
            @click="toggleDropdown">
      {{ label }}
    </button>
    <!-- Rendered in body so overflow of tables/containers can't clip or stretch it -->
    <Teleport to="body">
      <div ref="menu"
           class="dropdown-menu"
           :class="{ show: isOpen }"
           :style="menuStyle"
           @click="close">
        <slot/>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { defineProps, nextTick, onBeforeUnmount, ref } from 'vue';

defineProps({
  label: {
    type: String,
    default: 'Aktion',
  },
});

const GAP = 2;
const EDGE = 4;

const toggle    = ref(null);
const menu      = ref(null);
const isOpen    = ref(false);
const menuStyle = ref({});

const toggleDropdown = async () => {
  if (isOpen.value) {
    close();
    return;
  }

  isOpen.value = true;
  addListeners();
  await nextTick();
  positionMenu();
};

const close = () => {
  isOpen.value = false;
  removeListeners();
};

// Place the menu under the button; flip above it and shift left when there is no room
const positionMenu = () => {
  if (!toggle.value || !menu.value) return;

  const button = toggle.value.getBoundingClientRect();
  const width  = menu.value.offsetWidth;
  const height = menu.value.offsetHeight;

  let top = button.bottom + GAP;
  if (top + height > window.innerHeight - EDGE && button.top - height - GAP > EDGE) {
    top = button.top - height - GAP;
  }

  let left = button.left;
  if (left + width > window.innerWidth - EDGE) {
    left = Math.max(EDGE, button.right - width);
  }

  menuStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    right: 'auto',
    bottom: 'auto',
    margin: 0,
    zIndex: 5000,
  };
};

const handleOutsideClick = (event) => {
  const target = event.target;
  if (toggle.value?.contains(target) || menu.value?.contains(target)) return;
  close();
};

function addListeners() {
  document.addEventListener('click', handleOutsideClick);
  window.addEventListener('scroll', close, true);
  window.addEventListener('resize', close);
}

function removeListeners() {
  document.removeEventListener('click', handleOutsideClick);
  window.removeEventListener('scroll', close, true);
  window.removeEventListener('resize', close);
}

onBeforeUnmount(removeListeners);
</script>
